import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiMapPin, FiStar } from 'react-icons/fi';

const GBPCollection = ({ currentProject, onDataImported }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError('');
    setResults([]);

    try {
      const response = await fetch('/api/data/gbp/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId: currentProject.id,
          query: query.trim()
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setResults(data);
      } else {
        setError(data.error || 'Search failed');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (placeId) => {
    setImporting(true);
    setError('');

    try {
      const response = await fetch('/api/data/gbp/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId: currentProject.id,
          placeId
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        onDataImported(data.profile, data.reviews, data.media);
        setResults([]);
        setQuery('');
      } else {
        setError(data.error || 'Import failed');
      }
    } catch (err) {
      setError('Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 h-fit">
      <div className="flex items-center space-x-3 mb-4">
        <div className="p-2 bg-red-100 rounded-lg">
          <FiMapPin className="h-6 w-6 text-red-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Google Business Profile</h3>
          <p className="text-sm text-gray-600">Search and import business data from Google</p>
        </div>
      </div>

      <form onSubmit={handleSearch} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business name + suburb/city, Google Maps URL, or Place ID
          </label>
          <textarea
            className="input-field resize-none"
            rows="3"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g., 'Joe's Pizza Melbourne' or 'https://maps.google.com/...' or 'ChIJ...'"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !query.trim()}
          className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Searching GBP...
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <FiSearch className="h-4 w-4" />
              <span>Search GBP</span>
            </div>
          )}
        </button>
      </form>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <AnimatePresence>
        {results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 space-y-3"
          >
            <h4 className="font-medium text-gray-900">Search Results:</h4>
            {results.map((result, index) => (
              <motion.div
                key={result.placeId}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-3 border border-gray-200 rounded-lg"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">{result.name}</h5>
                    <p className="text-sm text-gray-600">{result.address}</p>
                    {result.rating && (
                      <div className="flex items-center space-x-1 mt-1">
                        <FiStar className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="text-sm text-gray-600">
                          {result.rating} ({result.ratingCount} reviews)
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleImport(result.placeId)}
                    disabled={importing}
                    className="ml-3 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importing ? 'Importing...' : 'Select'}
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GBPCollection;